<template>
    <section class="ai-review-workspace" :style="{'--review-chrome': chromeHeight + 'px'}">
        <header ref="toolbar" class="review-toolbar">
            <div class="toolbar-copy">
                <div class="toolbar-title">AI 평가 검토</div>
                <div class="toolbar-subtitle">AI 판단을 확인하고, 필요하면 최종 판단과 이유를 남겨 판단 기준을 개선합니다.</div>
            </div>
            <div class="toolbar-identity" aria-label="검토 대상 업체와 캠페인">
                <div class="identity-row">
                    <span class="identity-label">업체</span>
                    <strong class="identity-value" :title="identityCompanyText">{{ identityCompanyText }}</strong>
                </div>
                <div class="identity-row">
                    <span class="identity-label">캠페인</span>
                    <strong class="identity-value" :title="identityCampaignText">{{ identityCampaignText }}</strong>
                </div>
            </div>
            <b-button size="sm" variant="light" class="close-review" @click="$emit('close')">
                <fa icon="times" class="mr-1"/> 기존 평가 보기
            </b-button>
        </header>

        <section ref="contextHeader" class="shared-review-header" aria-label="AI 평가 검토 문맥">
            <div class="review-summary-row">
                <div class="summary-heading">
                    <strong>평가기준 검토</strong>
                    <span>{{ summary.completed }} / {{ summary.total }}</span>
                </div>
                <b-progress :max="summary.total || 1" height="7px" class="review-progress">
                    <b-progress-bar :value="summary.completed" variant="primary"/>
                </b-progress>
                <div class="summary-counts">
                    <span>미검토 <b>{{ summary.unreviewed }}</b></span>
                    <span>정정 <b class="text-primary">{{ summary.corrected }}</b></span>
                    <span>완료 <b>{{ summary.approved }}</b></span>
                    <span v-if="summary.failedCount">재요청 필요 <b class="text-danger">{{ summary.failedCount }}</b></span>
                    <span v-if="summary.retryingCount || summary.pendingCount">진행 중 <b>{{ summary.retryingCount + summary.pendingCount }}</b></span>
                    <span v-if="summary.heldCount">판정 보류 <b class="text-warning">{{ summary.heldCount }}</b></span>
                    <span v-if="summary.missingCount">AI 평가 없음 <b>{{ summary.missingCount }}</b></span>
                    <span v-if="reviewFlaggedCount" title="AI 판정과 별개로 사람이 확인할 점이 있는 평가기준 수">확인 필요 <b class="text-warning">{{ reviewFlaggedCount }}</b></span>
                    <span class="summary-percent">{{ summary.percent }}%</span>
                </div>
            </div>

            <div class="review-context-row">
                <div class="question-context">
                    <div class="eyebrow eyebrow--with-level">
                        <span>선택 문항</span>
                        <span v-if="hasLevelCodes && questionRollUp.level" class="level-badge-group">
                            <b-badge :variant="levelVariant(questionRollUp.level)" class="level-badge">
                                질문 수준 {{ levelLabel(questionRollUp.level) }}
                            </b-badge>
                            <b-badge v-if="questionRollUp.storedLevel !== questionRollUp.level"
                                     v-b-tooltip.hover
                                     variant="light"
                                     class="level-badge level-badge--stored"
                                     title="저장된 평가 결과 기준 값"
                            >저장 {{ levelLabel(questionRollUp.storedLevel) || '없음' }}</b-badge>
                        </span>
                    </div>
                    <div class="context-value context-value--question" :title="questionTitle">
                        <span v-if="questionContext.groupName">{{ questionContext.groupName }}</span>
                        <fa v-if="questionContext.groupName" icon="angle-right" class="context-chevron"/>
                        <span class="context-question-title">{{ questionTitle }}</span>
                    </div>
                </div>
                <div class="criterion-context">
                    <div class="eyebrow">현재 평가기준</div>
                    <div
                        v-if="selectedRow"
                        class="context-value context-value--criterion"
                        :title="selectedRow.name"
                    >
                        {{ selectedRow.name }}
                    </div>
                    <div v-else class="context-value context-value--criterion context-value--empty">
                        평가기준을 선택해 주세요.
                    </div>
                </div>
            </div>

            <!-- 점수 방식(레거시 v2) 캠페인: 관리수준 위젯 그대로. EV_COUNT 방식이면 아래 수준 바가 뜬다 -->
            <div v-if="managementLevel" class="management-level-row">
                <ManagementLevelTable v-bind="managementLevel"/>
            </div>
            <div v-else-if="hasLevelCodes && questionRollUp.total" class="question-level-row">
                <div class="qlevel-bar">
                    <div v-for="col in questionLevelColumns"
                         :key="col.code"
                         :class="['qlevel-cell', {'qlevel-cell--active': col.active}]"
                    >
                        <span class="qlevel-label">{{ col.label }}</span>
                        <b class="qlevel-count">{{ col.count }}</b>
                    </div>
                </div>
                <div class="qlevel-foot">
                    <span>문항 {{ questionRollUp.total }}개</span>
                    <span v-if="questionRollUp.unscored">미산정 {{ questionRollUp.unscored }}개</span>
                    <span class="qlevel-note">검토 중인 판단을 반영한 값입니다. 저장해야 점수에 반영됩니다.</span>
                </div>
            </div>
        </section>

        <div class="review-columns">
            <section class="answer-column review-column">
                <div class="answer-scroll">
                    <div v-if="answerSubs.length" class="answer-original">
                        <div
                            v-for="item in answerSubs"
                            :key="item.sub_id || item.sub_code"
                            class="answer-item"
                            :data-answer-sub-id="item.sub_id"
                        >
                            <sub-component
                                :sub_type="item.sub_type"
                                :item="item"
                                :is_disabled_by_parent="true"
                                :audit_mode="true"
                            />

                            <div v-if="attachmentsForAnswer(item).length" class="attachments-block">
                                <div class="section-title-row">
                                    <h5>첨부파일</h5>
                                    <span>{{ attachmentsForAnswer(item).length }}개</span>
                                </div>
                                <div class="attachment-list">
                                    <div v-for="file in attachmentsForAnswer(item)" :key="file.id" class="attachment-row">
                                        <div class="attachment-type">
                                            <fa :icon="file.attachment_type === 'url' ? 'link' : 'file-alt'"/>
                                        </div>
                                        <div class="attachment-copy">
                                            <strong :title="file.filename">{{ file.filename }}</strong>
                                            <span>{{ fileMeta(file) }}</span>
                                        </div>
                                        <div class="attachment-actions">
                                            <b-button
                                                v-b-tooltip.hover
                                                size="sm"
                                                variant="light"
                                                :title="file.attachment_type === 'url' ? '원본 열기' : '원본 다운로드'"
                                                :disabled="downloadingId === file.id"
                                                :aria-label="file.attachment_type === 'url' ? '원본 열기' : '원본 다운로드'"
                                                @click="downloadOriginal(file)"
                                            >
                                                <b-spinner v-if="downloadingId === file.id" small/>
                                                <fa v-else :icon="file.attachment_type === 'url' ? 'external-link-alt' : 'download'"/>
                                            </b-button>
                                            <span v-b-tooltip.hover :title="factsActionTitle(file)" class="attachment-action-tooltip">
                                                <b-button
                                                    size="sm"
                                                    variant="light"
                                                    :aria-label="factsActionTitle(file)"
                                                    :disabled="!canOpenFacts(file)"
                                                    @click="openFacts(file)"
                                                >
                                                    <fa icon="file-alt"/>
                                                </b-button>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-card">표시할 답변 원문이 없습니다.</div>
                </div>
            </section>

            <section class="criteria-column review-column">
                <div class="criteria-header">
                    <span>평가기준</span><span>AI 판단</span><span>검토 상태</span><span>정정된 판단</span>
                </div>
                <div class="criteria-scroll">
                    <div v-if="!groupedCriteria.length" class="empty-card">평가기준이 없습니다.</div>
                    <div v-for="group in groupedCriteria" :key="group.subCode" class="criteria-group">
                        <div class="criteria-group-title">
                            <span>
                                {{ group.label }}
                                <template v-if="subLevel(group.subCode).live">
                                    <b-badge :variant="levelVariant(subLevel(group.subCode).live)" class="level-badge ml-1">
                                        {{ levelLabel(subLevel(group.subCode).live) }}
                                    </b-badge>
                                    <b-badge v-if="subLevel(group.subCode).stored !== subLevel(group.subCode).live"
                                             v-b-tooltip.hover
                                             variant="light"
                                             class="level-badge level-badge--stored ml-1"
                                             title="저장된 평가 결과 기준 값"
                                    >저장 {{ levelLabel(subLevel(group.subCode).stored) || '없음' }}</b-badge>
                                </template>
                            </span>
                            <span class="criteria-group-right">
                                <!-- 해당없음은 문항(답변) 단위 — 이 답변의 AI 판단 전부를 한 번에 켜고 끈다 -->
                                <b-button v-if="groupNaState(group)"
                                          size="sm"
                                          :variant="groupNaState(group) === 'on' ? 'secondary' : 'outline-secondary'"
                                          class="apply-sub"
                                          :disabled="togglingNaSub !== null || applyingSub !== null"
                                          :title="groupNaState(group) === 'on' ? '이 답변의 해당없음을 해제합니다(평가요소별 판단으로 돌아감)' : '이 답변의 평가요소 AI 판단을 전부 해당없음으로 바꿉니다'"
                                          @click="toggleGroupNotApplicable(group)"
                                >
                                    <b-spinner v-if="togglingNaSub === group.subCode" small class="mr-1"/>{{ groupNaState(group) === 'on' ? '해당없음 해제' : '해당없음 처리' }}
                                </b-button>
                                <small>{{ group.rows.filter(row => ['approved', 'corrected'].includes(row.reviewState)).length }} / {{ group.rows.length }}</small>
                                <b-button v-if="group.rows.some(row => ['ready', 'held'].includes(row.itemState))"
                                          size="sm"
                                          variant="outline-primary"
                                          class="apply-sub"
                                          :disabled="applyingSub !== null"
                                          title="이 문항의 AI 판단(검토 정정 반영)을 평가 결과로 저장합니다"
                                          @click="applySub(group)"
                                >
                                    <b-spinner v-if="applyingSub === group.subCode" small class="mr-1"/>이 문항 적용
                                </b-button>
                            </span>
                        </div>
                        <button
                            v-for="row in group.rows"
                            :key="row.key"
                            type="button"
                            class="criteria-row"
                            :class="{selected: selectedKey === row.key}"
                            @click="selectRow(row, {scrollAnswer: true})"
                        >
                            <span class="criterion-name">
                                {{ row.name }}
                                <!-- 충족/미충족 각각의 수준을 모두 보여주고, 현재 판단 쪽만 색으로 강조 -->
                                <span v-if="rowLevels(row.key).hasCodes" class="criterion-levels">
                                    <b-badge v-if="rowLevels(row.key).active === 'na'"
                                             variant="secondary"
                                             class="criterion-level"
                                    >해당없음</b-badge>
                                    <template v-else>
                                        <b-badge :variant="rowLevels(row.key).active === 'pos' ? levelVariant(aggrOf(rowLevels(row.key).pos)) : 'light'"
                                                 :class="['criterion-level', {'criterion-level--muted': rowLevels(row.key).active !== 'pos'}]"
                                                 title="충족 시 수준"
                                        >충족 {{ levelTextOf(rowLevels(row.key).pos) }}</b-badge>
                                        <b-badge :variant="rowLevels(row.key).active === 'neg' ? levelVariant(aggrOf(rowLevels(row.key).neg)) : 'light'"
                                                 :class="['criterion-level', {'criterion-level--muted': rowLevels(row.key).active !== 'neg'}]"
                                                 title="미충족 시 수준"
                                        >미충족 {{ levelTextOf(rowLevels(row.key).neg) }}</b-badge>
                                    </template>
                                </span>
                                <!-- 확인 필요(review)는 충족 여부와 별개 — 판정은 옆 칸 그대로 -->
                                <span v-if="rowReviewFlags(row).length" class="review-flags">
                                    <span v-for="flag in rowReviewFlags(row)"
                                          :key="flag.code"
                                          class="review-flag"
                                          :title="flag.detail || flag.label"
                                    >{{ flag.label }}</span>
                                </span>
                            </span>
                            <span><span class="judgment-pill" :class="aiJudgmentClass(row)">{{ aiJudgmentText(row) }}</span></span>
                            <span><span class="state-pill" :class="row.reviewState">{{ reviewStateLabel(row.reviewState) }}</span></span>
                            <span class="corrected-cell">
                                <span v-if="row.reviewState === 'corrected'" class="judgment-pill" :class="judgmentClass(row.finalJudgment)">
                                    {{ judgmentLabel(row.finalJudgment) }}
                                </span>
                                <span v-else>—</span>
                            </span>
                        </button>
                    </div>
                </div>
                <div class="automatic-note">
                    <fa icon="info-circle"/>
                    문항 수준은 평가기준 결과로 자동 계산되며 이 화면에서 직접 수정하지 않습니다.
                </div>
            </section>

            <aside class="detail-column review-column">
                <template v-if="selectedRow">
                    <nav v-if="canChat" class="detail-tabs" aria-label="상세 패널 보기 전환">
                        <button type="button"
                                :class="{active: !showChat}"
                                :disabled="chatBusy"
                                @click="detailTab = 'review'"
                        >검토</button>
                        <button type="button"
                                :class="{active: showChat}"
                                @click="detailTab = 'chat'"
                        ><fa :icon="['fas', 'comments']" class="mr-1"/>AI 대화</button>
                    </nav>
                    <div v-if="showChat" class="detail-chat">
                        <AIQnaChatPanel :ai-audit-id="selectedRow.audit.id"
                                        :ev-id="selectedRow.evId"
                                        :ai-ev-item="selectedRow.aiItem"
                                        @busy-change="chatBusy = $event"
                                        @revision-applied="onQnaRevisionApplied"
                        />
                    </div>
                    <template v-else>
                        <section v-if="selectedItemState === 'failed' || selectedItemState === 'retrying'" class="ai-item-retry-panel">
                            <h4>{{ selectedItemState === 'retrying' ? 'AI 재평가 중' : 'AI 평가 실패' }}</h4>
                            <p>{{ selectedItemState === 'retrying' ? '이 평가기준만 다시 평가하고 있습니다.' : 'AI 판단이 생성되지 않아 검토할 수 없습니다.' }}</p>
                            <b-button :disabled="selectedItemState === 'retrying'" variant="primary" @click="retrySelectedItem">
                                다시 요청
                            </b-button>
                            <p v-if="retryError" class="text-danger mb-0">{{ retryError }}</p>
                        </section>
                        <div v-else-if="selectedItemState === 'pending'" class="ai-review-empty">
                            <b-spinner small/> 이 문항의 AI 평가를 진행하고 있습니다.
                        </div>
                        <div v-if="pollTimedOut && ['pending', 'retrying'].includes(selectedItemState)" class="poll-timeout-note">
                            <p>결과를 오래 기다리고 있어 자동 확인을 멈췄습니다. 배치 요청은 최대 24시간 걸릴 수 있습니다.</p>
                            <b-button size="sm" variant="outline-primary" @click="refreshUntilAuditSettles()">다시 불러오기</b-button>
                        </div>
                        <div v-else-if="selectedItemState === 'missing'" class="ai-review-empty">
                            <p>AI 평가가 아직 없습니다. 이 문항의 AI 평가를 먼저 요청해 주세요.</p>
                        </div>

                        <div v-if="isReviewable" class="detail-scroll">
                            <div class="judgment-comparison">
                                <div class="judgment-option judgment-option--ai">
                                    <span>AI 판단</span>
                                    <strong class="judgment-pill" :class="aiJudgmentClass(selectedRow)">
                                        {{ aiJudgmentText(selectedRow) }}
                                    </strong>
                                </div>
                                <div class="judgment-option final-judgment">
                                    <span>최종 판단</span>
                                    <!-- 해당없음은 평가요소별로 고르지 않는다 — 문항 헤더의 '해당없음 처리/해제'로만 바꾼다 -->
                                    <div v-if="reviewForm.finalJudgment === 'not_applicable'" class="na-final-note">
                                        해당없음 (문항 단위)
                                        <small>바꾸려면 평가기준 목록의 문항 헤더에서 '해당없음 해제'</small>
                                    </div>
                                    <b-form-radio-group
                                        v-else
                                        v-model="reviewForm.finalJudgment"
                                        class="final-judgment-options"
                                        buttons
                                        button-variant="outline-primary"
                                        size="sm"
                                    >
                                        <b-form-radio value="satisfied">충족</b-form-radio>
                                        <b-form-radio value="unsatisfied">미충족</b-form-radio>
                                    </b-form-radio-group>
                                </div>
                            </div>

                            <section v-if="selectedReviewFlags.length" class="review-flag-panel">
                                <div class="review-flag-panel-head">확인 필요 <small>AI 판단은 위와 같고, 아래 사유로 사람 확인이 필요합니다.</small></div>
                                <ul>
                                    <!-- 목록 행에 같은 배지가 있으니 여기선 설명만. 설명이 없는 사유는 배지 문구로 대신 -->
                                    <li v-for="flag in selectedReviewFlags" :key="flag.code" class="review-flag-note">
                                        {{ flag.detail || flag.label }}
                                    </li>
                                </ul>
                            </section>

                            <div v-if="isHeld" class="held-note">
                                <fa icon="info-circle"/>
                                AI가 이 평가기준의 충족 여부를 정하지 못해 사람 검토로 넘겼습니다(판정 보류).
                                아래 AI 판단 근거를 보고 최종 판단을 선택해 저장하세요.
                            </div>
                            <div v-else class="derived-result">
                                <span>검토 결과</span>
                                <b-badge :variant="isCorrection ? 'primary' : 'success'">
                                    {{ isCorrection ? '정정됨' : '승인됨' }}
                                </b-badge>
                                <small>AI 판단과 최종 판단을 비교해 자동으로 결정됩니다.</small>
                            </div>

                            <section v-if="selectedReason" class="ai-basis">
                                <div class="ai-basis-head">
                                    <span>AI 판단 근거</span>
                                    <b-badge v-if="selectedReasonCategory" variant="light">{{ selectedReasonCategory }}</b-badge>
                                </div>
                                <p v-if="selectedReason.comment" class="ai-basis-comment">{{ selectedReason.comment }}</p>
                                <!-- 정정 중에는 아래 '문제가 된 AI 근거'가 같은 목록을 선택형으로 보여준다 -->
                                <ul v-if="selectedEvidence.length && !showCorrectionFeedback" class="ai-basis-evidence">
                                    <li v-for="(evidence, index) in selectedEvidence" :key="`basis-${index}-${evidence.filename}`">
                                        <strong>{{ evidence.filename || '첨부파일' }}</strong>
                                        <span>{{ evidence.basis || '근거 설명 없음' }}</span>
                                    </li>
                                </ul>
                            </section>

                            <template v-if="showCorrectionFeedback">
                                <div class="feedback-section">
                                    <label>판단을 바꾼 핵심 이유 <b class="text-danger">(필수)</b></label>
                                    <small>한 개만 선택합니다. 같은 실수 방지에 활용됩니다.</small>
                                    <div class="reason-chips">
                                        <button
                                            v-for="reason in reasonOptions"
                                            :key="reason.code"
                                            type="button"
                                            :class="{selected: reviewForm.reasonCode === reason.code}"
                                            @click="selectReason(reason)"
                                        >{{ reason.label }}</button>
                                    </div>
                                    <div v-if="selectedReasonMini === 'range'" class="reason-mini">
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.doc" size="sm" placeholder="문서 범위"/>
                                        <span>↔</span>
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.target" size="sm" placeholder="요구 대상"/>
                                    </div>
                                    <div v-else-if="selectedReasonMini === 'period'" class="reason-mini">
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.docYear" size="sm" placeholder="문서 연도"/>
                                        <span>↔</span>
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.reqYear" size="sm" placeholder="요구 연도"/>
                                    </div>
                                    <div v-else-if="selectedReasonMini === 'quant'" class="reason-mini">
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.std" size="sm" placeholder="요구 기준"/>
                                        <span>↔</span>
                                        <b-form-input v-model.trim="reviewForm.reasonPayload.actual" size="sm" placeholder="확인 결과"/>
                                    </div>
                                </div>

                                <div class="feedback-section">
                                    <label>문제가 된 AI 근거</label>
                                    <small>복수 선택할 수 있으며 선택 항목은 판단 기준 개선 후보에 함께 저장됩니다.</small>
                                    <div class="evidence-list">
                                        <button
                                            v-for="(evidence, index) in selectedEvidence"
                                            :key="`${index}-${evidence.filename}`"
                                            type="button"
                                            :class="{selected: reviewForm.evidenceRefs.includes(index)}"
                                            @click="toggleEvidence(index)"
                                        >
                                            <b-form-checkbox :checked="reviewForm.evidenceRefs.includes(index)" tabindex="-1"/>
                                            <span><strong>{{ evidence.filename || '첨부파일' }}</strong><em>{{ evidence.basis || '근거 설명 없음' }}</em></span>
                                        </button>
                                        <button
                                            type="button"
                                            :class="{selected: reviewForm.evidenceRefs.includes('none')}"
                                            @click="toggleEvidence('none')"
                                        >
                                            <b-form-checkbox :checked="reviewForm.evidenceRefs.includes('none')" tabindex="-1"/>
                                            <span><strong>제출 자료에 해당 근거 없음</strong></span>
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <template v-if="showCorrectionFeedback || isHeld">
                                <div class="feedback-section">
                                    <label for="ai-review-comment">추가 설명 <span>(선택)</span></label>
                                    <b-form-textarea
                                        id="ai-review-comment"
                                        v-model.trim="reviewForm.comment"
                                        rows="3"
                                        max-rows="5"
                                        maxlength="2000"
                                        placeholder="필요한 경우 정정 이유를 한 줄로 남겨주세요."
                                    />
                                    <div class="character-count">{{ reviewForm.comment.length }} / 2000</div>
                                </div>
                            </template>

                            <div v-else-if="!isHeld" class="approval-note">
                                <fa icon="check-circle"/>
                                AI 판단과 최종 판단이 같습니다. 별도 이유 입력 없이 승인할 수 있습니다.
                            </div>
                        </div>

                        <!-- 문항 단위 AI 평가 요청. 평가가 없으면 펼쳐 두고, 있으면 접어 둔다(재요청은 드물다) -->
                        <details v-if="canRequestSub"
                                 :key="`request-${selectedRow.subCode}`"
                                 class="sub-request"
                                 :open="selectedItemState === 'missing'"
                        >
                            <summary>{{ selectedItemState === 'missing' ? '이 문항 AI 평가 요청' : '이 문항 AI 평가 다시 요청' }}</summary>
                                <b-form-select v-model="judgmentModel" size="sm" aria-label="판정 모델"
                                               :disabled="requestingSub || judgmentModelsLoading" class="mb-2">
                                    <b-form-select-option :value="null">{{ judgmentModelsLoading ? '판정 모델 목록을 불러오는 중...' : defaultJudgmentModelLabel }}</b-form-select-option>
                                    <b-form-select-option-group v-for="group in judgmentModelGroups" :key="group.provider" :label="group.label">
                                        <b-form-select-option v-for="model in group.models" :key="model.id" :value="model.id">{{ judgmentModelLabel(model) }}</b-form-select-option>
                                    </b-form-select-option-group>
                                </b-form-select>
                                <small v-if="selectedJudgmentModelNote" class="d-block text-muted">{{ selectedJudgmentModelNote }}</small>
                                <small v-if="judgmentModelsError" class="d-block text-warning">판정 모델 목록을 불러오지 못했습니다. 기본값으로 요청할 수 있습니다.</small>
                                <b-dropdown :disabled="requestingSub" variant="primary" :text="selectedItemState === 'missing' ? 'AI 평가 요청' : 'AI 평가 다시 요청'">
                                    <b-dropdown-item @click="requestSelectedSubEvaluation('immediate')">즉시</b-dropdown-item>
                                    <b-dropdown-item @click="requestSelectedSubEvaluation('batch')">배치 (최대 24시간)</b-dropdown-item>
                                </b-dropdown>
                        </details>

                        <div class="detail-actions">
                            <small v-if="isReviewable"><fa icon="info-circle"/> 저장된 피드백은 검증 후 판단 기준 개선에 활용됩니다.</small>
                            <div class="detail-action-buttons">
                                <b-button
                                    variant="outline-primary"
                                    :disabled="saving || summary.nextUnreviewedIndex < 0"
                                    title="단축키: Alt + N"
                                    aria-keyshortcuts="Alt+N"
                                    @click="goNextUnreviewed"
                                >
                                    다음 미검토 <fa icon="arrow-right" class="ml-1"/>
                                </b-button>
                                <b-button
                                    v-if="isReviewable"
                                    variant="primary"
                                    :disabled="saving || !canSave"
                                    title="단축키: Ctrl + Enter"
                                    aria-keyshortcuts="Control+Enter"
                                    @click="saveAndNext"
                                >
                                    <b-spinner v-if="saving" small class="mr-1"/>
                                    검토 저장 후 다음 <fa v-if="!saving" icon="arrow-right" class="ml-1"/>
                                </b-button>
                            </div>
                        </div>
                    </template>
                </template>
                <div v-else class="empty-detail">
                    <fa icon="mouse-pointer"/>
                    <strong>평가기준을 선택하세요.</strong>
                    <span>왼쪽 목록에서 검토할 평가기준을 선택하면 상세 판단과 AI 근거를 확인할 수 있습니다.</span>
                </div>
            </aside>
        </div>

        <b-modal v-model="factsModal" size="xl" scrollable ok-only ok-title="닫기" :title="factsTitle">
            <div v-if="factsLoading" class="text-center py-5"><b-spinner/></div>
            <div v-else-if="!factsEntries.length" class="empty-card">추출된 원본 내용이 없습니다.</div>
            <div v-else class="facts-viewer">
                <section v-for="(entry, entryIndex) in factsEntries" :key="entryIndex" class="fact-entry">
                    <div class="fact-entry-title">
                        <fa icon="file-alt"/>
                        <strong>{{ entry.inner_file_key || factsTitle }}</strong>
                        <b-badge v-if="entry.language" variant="light">{{ entry.language }}</b-badge>
                    </div>
                    <div v-for="(fact, factIndex) in entry.facts" :key="factIndex" class="fact-row">
                        <span>{{ fact.page_or_location || '위치 정보 없음' }}</span>
                        <p>{{ fact.content }}</p>
                    </div>
                </section>
            </div>
        </b-modal>
    </section>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {mapActions, mapGetters, mapState} from 'vuex';
import subComponent from '@core_components/admin/components/subs/SubComponent.vue';
import ManagementLevelTable from '@shared_components/common/ManagementLevelTable.vue';
import AIQnaChatPanel from '@shared/components/admin/pages/Management/AIAssessment/_SubComponent/AIQnaChatPanel.vue';
import judgmentModelsMixin from '@/mixins/judgmentModels';

const {
    normalizeJudgment,
    ratingToFinalJudgment,
    rowReviewState,
    groupNotApplicableState,
    buildReviewContextSignature,
    toggleEvidenceReference,
    buildRatingPayload,
    summarizeReviews,
    selectLatestAudit,
    getAiEvaluationItemState,
    auditPollDelay,
    normalizeItemRetryError,
    reviewFlags,
    countReviewFlagged,
    attachmentsForSub,
    hasExtractedFacts,
    getReviewKeyboardAction,
    formatReviewIdentityValue,
    scrollAnswerContainerToSub,
    LEVEL_ORDER,
    buildEvCodeIndex,
    aggregateEvCodes,
    resolveCriterionLevels,
    computeSubLevel,
    rollUpQuestionLevels,
    buildApplyEvList,
} = require('./ai-evaluation-review-presentation.cjs');

export default {
    name: 'AIEvaluationReviewWorkspaceV2',
    components: {subComponent, ManagementLevelTable, AIQnaChatPanel},
    mixins: [judgmentModelsMixin],
    props: {
        subs: {type: Array, default: () => []},
        evResults: {type: Object, default: () => ({})},
        latestAudits: {type: Object, default: () => ({})},
        questionContext: {type: Object, default: () => ({})},
        reviewIdentity: {type: Object, default: () => ({})},
        managementLevel: {type: Object, default: null},
    },
    data() {
        return {
            selectedKey: null,
            saving: false,
            downloadingId: null,
            factsModal: false,
            factsLoading: false,
            factsTitle: '',
            factsData: null,
            factsAvailability: {},
            requestingSub: false,
            retryError: null,
            chromeHeight: 256,
            chromeObserver: null,
            applyingSub: null,
            togglingNaSub: null,
            detailTab: 'review', // 'review' | 'chat' — 평가기준을 바꿔도 유지한다
            chatBusy: false,
            pollTimer: null,
            pollAttempts: 0,
            polling: false,
            pollTimedOut: false,
            reviewForm: this.emptyReviewForm(),
            reasonOptions: [
                {code: 'NOT_IN_DOC', label: '제출 자료에 해당 내용 없음'},
                {code: 'SCOPE', label: '적용 범위 불일치', mini: 'range'},
                {code: 'EXPIRED', label: '기간 경과', mini: 'period'},
                {code: 'QUANT', label: '정량 기준 미달', mini: 'quant'},
                {code: 'NO_EXEC', label: '정책만 있고 실행 증빙 없음'},
                {code: 'ETC', label: '기타'},
            ],
        };
    },
    computed: {
        ...mapState(['question_composition']),
        ...mapState('evaluations', ['evaluation_list', 'ev_code_list', 'map_ev_lv_variant']),
        ...mapState('ai_assessment', ['aiEvData', 'myRatings']),
        ...mapState('attachments', ['attachments']),
        ...mapGetters('ai_assessment', ['getMyRating']),
        ...mapGetters('evaluations', ['mapEvCodeLv']),
        evCodeIndex() {
            return buildEvCodeIndex(this.ev_code_list);
        },
        // 수준 코드가 하나도 없으면 점수 방식(레거시 v2) 캠페인이므로 수준 UI 를 띄우지 않는다
        hasLevelCodes() {
            return this.criteriaRows.some(row => row.criterion
                && (row.criterion.pos_ev_code || row.criterion.neg_ev_code));
        },
        // 저장 전 라디오 선택까지 반영한 판단 — 이게 있어야 검토 중에 수준이 즉시 움직인다.
        // criteriaRows 는 reviewForm 을 읽지 않으므로 순환하지 않는다.
        effectiveJudgments() {
            const judgments = {};
            this.criteriaRows.forEach(row => { judgments[row.key] = row.finalJudgment; });
            if (this.selectedKey && this.reviewForm.finalJudgment) {
                judgments[this.selectedKey] = this.reviewForm.finalJudgment;
            }
            return judgments;
        },
        rowLevelMap() {
            const map = {};
            this.criteriaRows.forEach(row => {
                map[row.key] = resolveCriterionLevels(row.criterion, this.effectiveJudgments[row.key], this.evCodeIndex);
            });
            return map;
        },
        subLevelMap() {
            const map = {};
            this.groupedCriteria.forEach(group => {
                const levels = group.rows.map(row => this.rowLevelMap[row.key]).filter(Boolean);
                map[group.subCode] = computeSubLevel(levels, this.evCodeIndex);
            });
            return map;
        },
        questionRollUp() {
            const live = [];
            const stored = [];
            this.groupedCriteria.forEach(group => {
                const level = this.subLevelMap[group.subCode] || {};
                live.push(level.live || null);
                stored.push(level.stored || null);
            });
            const rollUp = rollUpQuestionLevels(live, this.evCodeIndex);
            rollUp.storedLevel = aggregateEvCodes(stored, this.evCodeIndex);
            return rollUp;
        },
        questionLevelColumns() {
            return LEVEL_ORDER.map(code => ({
                code,
                label: this.levelLabel(code) || code,
                count: this.questionRollUp.counts[code] || 0,
                active: this.questionRollUp.level === code,
            }));
        },
        questionTitle() {
            // 문항 미선택이면 question_contents 가 [] 라 그대로 찍히지 않게 문자열만 쓴다
            // (AI 평가 관리의 [검토]는 문항 없이 이 화면을 연다).
            const {question_code, question_name, question_contents} = this.question_composition || {};
            if (!question_code) return '왼쪽 문항 목록에서 검토할 문항을 선택하세요';
            const name = [question_name, question_contents].find(value => typeof value === 'string' && value);
            return name || '선택한 문항';
        },
        identityCompanyText() {
            return formatReviewIdentityValue(
                this.reviewIdentity.companyName,
                this.reviewIdentity.companyId,
            );
        },
        identityCampaignText() {
            return formatReviewIdentityValue(
                this.reviewIdentity.campaignName,
                this.reviewIdentity.campaignId,
            );
        },
        answerSubs() {
            return this.subs.filter(item => item.sub_type !== 'file_URL2');
        },
        answerAttachmentIdsSignature() {
            return this.answerSubs
                .flatMap(item => attachmentsForSub(this.attachments, item.sub_id))
                .map(file => file.id)
                .filter(id => id !== null && id !== undefined)
                .sort((left, right) => Number(left) - Number(right))
                .join(',');
        },
        criteriaRows() {
            const rows = [];
            this.subs.forEach((sub, subIndex) => {
                const audit = this.latestAuditForSub(sub.sub_id);
                const results = this.evResults?.[sub.sub_code]?.ev_results || [];
                results.forEach((result, resultIndex) => {
                    const info = (this.evaluation_list?.[sub.sub_code] || []).find(item => String(item.ev_id) === String(result.ev_id)) || {};
                    const aiItem = audit?.valid_map?.[result.ev_id] || audit?.valid_map?.[String(result.ev_id)] || null;
                    const aiJudgment = normalizeJudgment(aiItem);
                    const rating = audit?.id == null ? null : this.getMyRating(audit.id, result.ev_id);
                    const finalJudgment = ratingToFinalJudgment(aiJudgment, rating);
                    const itemState = getAiEvaluationItemState(aiItem, audit, result.ev_id);
                    rows.push({
                        key: `${sub.sub_code}-${result.ev_id}`,
                        subCode: sub.sub_code,
                        subId: sub.sub_id,
                        subLabel: sub.label || sub.sub_name || sub.sub_code,
                        groupOrder: subIndex,
                        resultOrder: resultIndex,
                        evId: result.ev_id,
                        name: info.name || info.ev_name || result.ev_name || '평가기준',
                        audit,
                        aiItem,
                        aiJudgment,
                        rating,
                        finalJudgment,
                        criterion: result,
                        itemState,
                        reviewState: rowReviewState(itemState, aiJudgment, finalJudgment, rating),
                    });
                });
            });
            return rows;
        },
        selectedRow() {
            return this.criteriaRows.find(row => row.key === this.selectedKey) || null;
        },
        selectedItemState() {
            return this.selectedRow?.itemState || 'missing';
        },
        // 후속 대화는 실제 AI 판정(reason)이 있는 평가기준만 이어받을 수 있다 (없으면 서버 422).
        canChat() {
            return this.isReviewable
                && this.selectedRow?.audit?.id != null
                && this.selectedRow?.aiItem?.reason != null;
        },
        showChat() {
            return this.canChat && this.detailTab === 'chat';
        },
        groupedCriteria() {
            const groups = [];
            this.criteriaRows.forEach(row => {
                let group = groups.find(item => item.subCode === row.subCode);
                if (!group) {
                    group = {subCode: row.subCode, label: row.subLabel, rows: []};
                    groups.push(group);
                }
                group.rows.push(row);
            });
            return groups;
        },
        summary() {
            return summarizeReviews(this.criteriaRows, this.selectedRowIndex);
        },
        reviewFlaggedCount() {
            return countReviewFlagged(this.criteriaRows.map(row => row.aiItem));
        },
        selectedReviewFlags() {
            return reviewFlags(this.selectedRow?.aiItem);
        },
        selectedRowIndex() {
            return this.criteriaRows.findIndex(row => row.key === this.selectedKey);
        },
        canRequestSub() {
            return ['missing', 'ready', 'held'].includes(this.selectedItemState);
        },
        isHeld() {
            return this.selectedItemState === 'held';
        },
        // AI 판단이 있는 항목과 AI 가 판정을 보류한 항목은 평가자가 판단을 저장할 수 있다
        isReviewable() {
            return ['ready', 'held'].includes(this.selectedItemState);
        },
        isCorrection() {
            return Boolean(this.selectedRow && this.reviewForm.finalJudgment !== this.selectedRow.aiJudgment);
        },
        // 보류 항목은 바꿀 AI 판단이 없으므로 '바꾼 이유'·'문제가 된 근거'를 묻지 않는다
        showCorrectionFeedback() {
            return !this.isHeld && this.isCorrection;
        },
        canSave() {
            if (!this.isReviewable) return false;
            if (!this.selectedRow?.audit?.id || !this.reviewForm.finalJudgment) return false;
            if (this.isHeld) return true;
            if (!this.selectedRow.aiJudgment) return false;
            return !this.isCorrection || Boolean(this.reviewForm.reasonCode);
        },
        selectedReason() {
            const reason = this.selectedRow?.aiItem?.reason;
            return reason && typeof reason === 'object' ? reason : null;
        },
        selectedReasonCategory() {
            const category = this.selectedReason?.category;
            return category?.category || category?.code || '';
        },
        selectedEvidence() {
            const evidence = this.selectedRow?.aiItem?.reason?.evidence;
            return Array.isArray(evidence) ? evidence : [];
        },
        selectedReasonMini() {
            return this.reasonOptions.find(reason => reason.code === this.reviewForm.reasonCode)?.mini || null;
        },
        factsEntries() {
            return Array.isArray(this.factsData?.entries) ? this.factsData.entries : [];
        },
        reviewContextSignature() {
            return buildReviewContextSignature(this.selectedRow);
        },
        auditIdsSignature() {
            return [...new Set(this.criteriaRows.map(row => row.audit?.id).filter(id => id != null))]
                .sort((left, right) => Number(left) - Number(right))
                .join(',');
        },
        selectedAuditActivitySignature() {
            return `${this.selectedRow?.audit?.id || ''}:${this.selectedRow?.audit?.status || ''}:${this.selectedRow?.audit?.retry_target_ev_id ?? ''}`;
        },
    },
    watch: {
        criteriaRows: {
            immediate: true,
            handler(rows) {
                if (!rows.length) {
                    this.selectedKey = null;
                    return;
                }
                if (!rows.some(row => row.key === this.selectedKey)) {
                    this.selectRow(rows.find(row => row.reviewState === 'unreviewed') || rows[0]);
                }
            },
        },
        auditIdsSignature: {
            immediate: true,
            handler() {
                this.loadRatingsForVisibleAudits();
            },
        },
        answerAttachmentIdsSignature: {
            immediate: true,
            handler() {
                this.loadFactsAvailability();
            },
        },
        reviewContextSignature: {
            immediate: true,
            handler() {
                this.syncReviewForm();
            },
        },
        selectedAuditActivitySignature: {
            immediate: true,
            handler() {
                if (!this.polling && ['requested', 'processing', 'awaiting_ai', 'retrying'].includes(this.selectedRow?.audit?.status)) {
                    this.refreshUntilAuditSettles();
                }
            },
        },
    },
    mounted() {
        window.addEventListener('keydown', this.handleReviewShortcut);
        this.observeChromeHeight();
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleReviewShortcut);
        this.stopAuditPolling();
        if (this.chromeObserver) this.chromeObserver.disconnect();
    },
    methods: {
        ...mapActions('industry_scores', ['saveQeEvaluateResult']),
        ...mapActions('ai_assessment', [
            'fetchMyRatingsForAudits',
            'updateMyRatingCache',
            'requestSubAiEvaluation',
            'retryAiEvaluationItem',
            'refreshSubAiEvData',
        ]),
        // 적용은 기존 사람 판정을 덮어쓰고 바로 저장되므로 바뀌는 내용과 미검토 행 수를 보여주고 확인받는다
        async confirmApplySub(group, evList) {
            const current = this.evResults?.[group.subCode]?.ev_results || [];
            const stateText = ({satisfied, not_applicable}) => {
                if (not_applicable === true) return '해당없음';
                if (satisfied === true) return '충족';
                if (satisfied === false) return '미충족';
                return '미판정';
            };
            const changes = evList.map(entry => {
                const row = group.rows.find(item => String(item.evId) === String(entry.ev_id));
                const before = stateText(current.find(ev => String(ev.ev_id) === String(entry.ev_id)) || {});
                const after = stateText(entry);
                return {name: row?.name || entry.ev_id, before, after};
            }).filter(change => change.before !== change.after);
            const unreviewed = group.rows.filter(row => row.itemState === 'ready' && row.reviewState === 'unreviewed').length;
            const h = this.$createElement;
            return this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: 'AI 평가 적용',
                message: h('div', {}, [
                    h('div', {}, `${group.label} 문항의 평가요소 ${evList.length}개에 AI 판단을 적용해 저장합니다. 기존 평가자 판정이 바뀌며, 결과보고서와 점수에 바로 반영됩니다.`),
                    unreviewed ? h('div', {class: 'text-danger small mt-2'}, `검토하지 않은 AI 판단 ${unreviewed}개가 포함되어 있습니다.`) : null,
                    changes.length
                        ? h('ul', {class: 'small mt-2 mb-0'}, changes.map(change => h('li', {}, `${change.name}: ${change.before} → ${change.after}`)))
                        : h('div', {class: 'small mt-2'}, '판정이 바뀌는 평가요소는 없습니다.'),
                ]),
            });
        },
        // 문항의 AI 판단을 평가 결과로 저장한다(사람 평가와 같은 경로).
        // 검토 화면의 수준 표시는 여기까지 해야 "미리보기"가 아니라 저장본이 된다.
        async applySub(group) {
            if (this.applyingSub) return;
            const {pc, project_code, cmp, campaign_id, cpn, company_id} = this.$route.query;
            const targetCompanyId = Number(cpn != null ? cpn : company_id);
            const evList = buildApplyEvList(group.rows, this.effectiveJudgments, {
                companyId: targetCompanyId,
                authorId: this.$store.state.user_id,
            });
            if (!evList.length) {
                this.$EventBus.$emit('toast-failure', 'AI 평가 적용', '적용할 AI 판단이 없습니다.');
                return;
            }
            if (!(await this.confirmApplySub(group, evList))) return;
            this.applyingSub = group.subCode;
            try {
                await this.saveQeEvaluateResult({
                    project_code: pc != null ? pc : project_code,
                    campaign_id: Number(cmp != null ? cmp : campaign_id),
                    company_id: targetCompanyId,
                    sub_code: group.subCode,
                    ev_list: evList,
                    from_v3: true,
                });
                this.$emit('applied', group.subCode);
                this.$EventBus.$emit('toast-success', 'AI 평가 적용', `${group.label} 문항의 AI 평가를 적용했습니다.`);
            } catch (error) {
                const message = getErrorMessage(error, '평가 결과를 저장하지 못했습니다.');
                this.$EventBus.$emit('toast-failure', 'AI 평가 적용 실패', message);
            } finally {
                this.applyingSub = null;
            }
        },
        // 상단(툴바+헤더) 실제 높이를 재서 본문 스크롤 높이에 반영한다.
        // 수준 바나 관리수준 위젯이 붙으면 헤더가 커지는데, 고정값이면 본문이 화면 밖으로 밀린다.
        observeChromeHeight() {
            const measure = () => {
                const toolbar = this.$refs.toolbar ? this.$refs.toolbar.offsetHeight : 0;
                const header = this.$refs.contextHeader ? this.$refs.contextHeader.offsetHeight : 0;
                if (toolbar + header > 0) this.chromeHeight = toolbar + header;
            };
            measure();
            if (typeof ResizeObserver === 'undefined') return;
            this.chromeObserver = new ResizeObserver(measure);
            if (this.$refs.toolbar) this.chromeObserver.observe(this.$refs.toolbar);
            if (this.$refs.contextHeader) this.chromeObserver.observe(this.$refs.contextHeader);
        },
        emptyReviewForm() {
            return {
                finalJudgment: null,
                reasonCode: null,
                reasonPayload: {},
                evidenceRefs: [],
                comment: '',
            };
        },
        // 집계 수준 코드(U/D/C/B/A/N) → 표시 라벨/색. 구 화면 UserEvaluationComponentV2 와 같은 매핑
        levelLabel(aggrCode) {
            const meta = aggrCode ? this.mapEvCodeLv[aggrCode] : null;
            return meta ? meta.description : null;
        },
        levelVariant(aggrCode) {
            const variant = aggrCode ? this.map_ev_lv_variant[aggrCode] : null;
            return (variant && variant.variant) || 'secondary';
        },
        levelTextOf(codeMeta) {
            return codeMeta ? codeMeta.label : '—';
        },
        aggrOf(codeMeta) {
            return codeMeta ? codeMeta.aggrCode : null;
        },
        rowLevels(key) {
            return this.rowLevelMap[key] || {};
        },
        subLevel(subCode) {
            return this.subLevelMap[subCode] || {};
        },
        latestAuditForSub(subId) {
            const selected = this.latestAudits?.[subId] || this.latestAudits?.[String(subId)];
            const audits = Object.values(this.aiEvData?.[subId] || this.aiEvData?.[String(subId)] || {});
            return selectLatestAudit(selected ? [...audits, selected] : audits);
        },
        async loadRatingsForVisibleAudits() {
            const ids = [...new Set(this.criteriaRows.map(row => row.audit?.id).filter(id => id != null))];
            if (!ids.length) return;
            await this.fetchMyRatingsForAudits({ids});
        },
        attachmentsForAnswer(item) {
            return attachmentsForSub(this.attachments, item?.sub_id);
        },
        async loadFactsAvailability() {
            const files = this.answerSubs.flatMap(item => this.attachmentsForAnswer(item));
            const uniqueFiles = [...new Map(files.map(file => [String(file.id), file])).values()];
            await Promise.all(uniqueFiles.map(async file => {
                if (file.attachment_type === 'url') {
                    this.$set(this.factsAvailability, file.id, false);
                    return;
                }
                this.$set(this.factsAvailability, file.id, null);
                try {
                    const {data} = await this.$axios.get(`/document-facts/${file.id}`);
                    this.$set(this.factsAvailability, file.id, hasExtractedFacts(data));
                } catch (error) {
                    this.$set(this.factsAvailability, file.id, false);
                }
            }));
        },
        canOpenFacts(file) {
            return file.attachment_type !== 'url' && this.factsAvailability[file.id] === true;
        },
        factsActionTitle(file) {
            if (file.attachment_type === 'url') return 'URL은 추출된 원본을 제공하지 않습니다.';
            if (this.factsAvailability[file.id] === null || this.factsAvailability[file.id] === undefined) {
                return '추출 여부 확인 중';
            }
            return this.canOpenFacts(file) ? '추출된 원본 확인' : '추출된 원본 없음';
        },
        selectRow(row, {scrollAnswer = false} = {}) {
            this.selectedKey = row?.key || null;
            this.retryError = null;
            if (scrollAnswer && row?.subId != null) {
                this.scrollAnswerToSub(row.subId);
            }
        },
        scrollAnswerToSub(subId) {
            this.$nextTick(() => {
                const container = this.$el.querySelector('.answer-scroll');
                scrollAnswerContainerToSub(container, subId);
            });
        },
        syncReviewForm() {
            const row = this.selectedRow;
            if (!row) {
                this.reviewForm = this.emptyReviewForm();
                return;
            }
            const rating = row.rating || {};
            const evidenceRef = Array.isArray(rating.evidence_ref)
                ? rating.evidence_ref
                : (rating.evidence_ref === null || rating.evidence_ref === undefined ? [] : [rating.evidence_ref]);
            this.reviewForm = {
                finalJudgment: ratingToFinalJudgment(row.aiJudgment, row.rating),
                reasonCode: rating.reason_code || null,
                reasonPayload: {...(rating.reason_payload || {})},
                evidenceRefs: evidenceRef,
                comment: rating.comment || '',
            };
        },
        selectReason(reason) {
            this.reviewForm.reasonCode = reason.code;
            if (reason.mini === 'range') this.reviewForm.reasonPayload = {doc: '', target: ''};
            else if (reason.mini === 'period') this.reviewForm.reasonPayload = {docYear: '', reqYear: ''};
            else if (reason.mini === 'quant') this.reviewForm.reasonPayload = {std: '', actual: ''};
            else this.reviewForm.reasonPayload = {};
        },
        toggleEvidence(ref) {
            this.reviewForm.evidenceRefs = toggleEvidenceReference(this.reviewForm.evidenceRefs, ref);
        },
        async retrySelectedItem() {
            if (this.selectedItemState !== 'failed' || !this.selectedRow?.audit?.id) return;
            this.retryError = null;
            try {
                await this.retryAiEvaluationItem({
                    auditId: this.selectedRow.audit.id,
                    evId: this.selectedRow.evId,
                });
                await this.refreshUntilAuditSettles();
            } catch (error) {
                this.retryError = normalizeItemRetryError(error);
            }
        },
        async requestSelectedSubEvaluation(judgmentMode = 'immediate') {
            if (!this.canRequestSub || this.requestingSub) return;
            if (this.selectedItemState !== 'missing') {
                const ok = await this.$bvModal.msgBoxConfirm(
                    `${this.selectedRow.subLabel} 문항의 AI 평가를 새로 요청합니다. 새 결과가 나오면 이 문항의 검토는 새 결과 기준으로 다시 해야 합니다.`,
                    {title: 'AI 평가 다시 요청', okTitle: '다시 요청', cancelTitle: '취소', centered: true},
                );
                if (!ok) return;
            }
            this.requestingSub = true;
            try {
                await this.requestSubAiEvaluation({
                    companyId: this.$route.query.cpn,
                    campaignId: this.$route.query.cmp,
                    subCode: this.selectedRow.subCode,
                    judgmentModel: this.judgmentModel,
                    judgmentMode,
                });
                await this.refreshUntilAuditSettles();
            } catch (error) {
                const message = error?.response?.status === 400 && String(getErrorMessage(error, '')).includes('judgment_model')
                    ? '선택한 판정 모델을 사용할 수 없습니다. 드롭다운에서 다시 선택해주세요.'
                    : getErrorMessage(error, 'AI 평가 요청 중 오류가 발생했습니다.');
                this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', message);
            } finally {
                this.requestingSub = false;
            }
        },
        async refreshUntilAuditSettles() {
            if (this.polling) return null;
            this.polling = true;
            this.pollAttempts = 0;
            this.pollTimedOut = false;
            const startedAt = Date.now();
            return new Promise((resolve) => {
                const tick = async () => {
                    const subId = this.selectedRow?.subId;
                    if (subId == null) {
                        this.stopAuditPolling();
                        resolve(null);
                        return;
                    }
                    this.pollAttempts += 1;
                    try {
                        await this.refreshSubAiEvData({subId});
                    } catch (error) {
                        this.stopAuditPolling();
                        resolve(null);
                        return;
                    }
                    const audit = this.latestAuditForSub(subId);
                    const active = ['requested', 'processing', 'awaiting_ai', 'retrying'].includes(audit?.status);
                    const delay = auditPollDelay(this.pollAttempts, Date.now() - startedAt);
                    if (!active || delay === null) {
                        this.pollTimedOut = active;
                        this.stopAuditPolling();
                        resolve(audit);
                        return;
                    }
                    this.pollTimer = setTimeout(tick, delay);
                };
                tick();
            });
        },
        stopAuditPolling() {
            if (this.pollTimer) clearTimeout(this.pollTimer);
            this.pollTimer = null;
            this.polling = false;
        },
        async saveAndNext() {
            if (!this.canSave || this.saving) return;
            const row = this.selectedRow;
            try {
                this.saving = true;
                const payload = buildRatingPayload({
                    aiJudgment: row.aiJudgment,
                    finalJudgment: this.reviewForm.finalJudgment,
                    reasonCode: this.reviewForm.reasonCode,
                    reasonPayload: this.reviewForm.reasonPayload,
                    evidenceRefs: this.reviewForm.evidenceRefs,
                    comment: this.reviewForm.comment,
                });
                const {data} = await this.$axios.put(
                    `/v2/ai-evaluation/${row.audit.id}/items/${row.evId}/rating`,
                    payload,
                );
                await this.updateMyRatingCache({ai_audit_id: row.audit.id, ev_id: row.evId, rating: data});
                const savedMessage = row.itemState === 'held'
                    ? '보류된 항목의 검토 판단을 저장했습니다.'
                    : (this.isCorrection ? '정정 피드백을 저장했습니다.' : 'AI 판단을 승인했습니다.');
                this.$EventBus.$emit('toast-success', 'AI 평가 검토 저장', savedMessage);
                await this.$nextTick();
                this.goNextUnreviewed();
            } catch (error) {
                const message = getErrorMessage(error, '검토 결과를 저장하지 못했습니다.');
                this.$EventBus.$emit('toast-failure', 'AI 평가 검토 저장 실패', message);
            } finally {
                this.saving = false;
            }
        },
        // 대화에서 수정안을 적용하면 AI 판정(valid_json)이 바뀌므로 해당 문항 AI 결과를 다시 불러온다.
        groupNaState(group) {
            return groupNotApplicableState(group?.rows);
        },
        async toggleGroupNotApplicable(group) {
            const state = this.groupNaState(group);
            const audit = group.rows.find(row => row.audit?.id != null)?.audit;
            if (!state || !audit || this.togglingNaSub) return;
            const turnOn = state !== 'on';
            const ok = await this.$bvModal.msgBoxConfirm(
                turnOn
                    ? `${group.label} 답변의 평가요소 AI 판단 ${group.rows.length}개를 전부 해당없음으로 바꿉니다.`
                    : `${group.label} 답변의 해당없음을 해제합니다. 원래 AI 판단이 있던 평가요소는 되돌아가고, 처음부터 해당없음이던 평가요소는 평가자가 충족/미충족을 골라야 합니다.`,
                {
                    title: turnOn ? '해당없음 처리' : '해당없음 해제',
                    okTitle: turnOn ? '해당없음 처리' : '해제',
                    cancelTitle: '취소',
                    centered: true,
                },
            );
            if (!ok) return;
            this.togglingNaSub = group.subCode;
            try {
                await this.$axios.put(`/assessments/ai-audit/${audit.id}/not-applicable`, {not_applicable: turnOn});
                await this.refreshSubAiEvData({subId: group.rows[0].subId});
                this.$EventBus.$emit('toast-success', 'AI 평가 해당없음',
                    turnOn ? `${group.label} 답변을 해당없음으로 바꿨습니다.` : `${group.label} 답변의 해당없음을 해제했습니다.`);
            } catch (error) {
                const message = getErrorMessage(error, '해당없음을 바꾸지 못했습니다.');
                this.$EventBus.$emit('toast-failure', 'AI 평가 해당없음', message);
            } finally {
                this.togglingNaSub = null;
            }
        },
        async onQnaRevisionApplied() {
            const subId = this.selectedRow?.subId;
            if (subId == null) return;
            try {
                await this.refreshSubAiEvData({subId});
            } catch (error) {
                this.$EventBus.$emit('toast-failure', 'AI 평가', '수정된 AI 판단을 다시 불러오지 못했습니다. 새로고침해 주세요.');
            }
        },
        goNextUnreviewed() {
            const index = summarizeReviews(this.criteriaRows, this.selectedRowIndex).nextUnreviewedIndex;
            if (index >= 0) this.selectRow(this.criteriaRows[index], {scrollAnswer: true});
        },
        handleReviewShortcut(event) {
            const action = getReviewKeyboardAction(event);
            if (action === 'next-unreviewed') {
                if (this.saving || this.summary.nextUnreviewedIndex < 0) return;
                event.preventDefault();
                this.goNextUnreviewed();
                return;
            }
            // 대화 탭에서는 Ctrl+Enter 가 질문 전송이다 — 검토 저장이 같이 실행되면 안 된다.
            if (action === 'save-and-next' && !this.showChat && !this.saving && this.canSave) {
                event.preventDefault();
                this.saveAndNext();
            }
        },
        judgmentLabel(value) {
            return {satisfied: '충족', unsatisfied: '미충족', not_applicable: '해당없음'}[value] || '미평가';
        },
        judgmentClass(value) {
            return `judgment-${value || 'pending'}`;
        },
        rowReviewFlags(row) {
            return reviewFlags(row?.aiItem);
        },
        aiJudgmentText(row) {
            return row?.itemState === 'held' ? '판정 보류' : this.judgmentLabel(row?.aiJudgment);
        },
        aiJudgmentClass(row) {
            return row?.itemState === 'held' ? 'judgment-held' : this.judgmentClass(row?.aiJudgment);
        },
        reviewStateLabel(value) {
            return {
                unreviewed: '미검토',
                approved: '승인됨',
                corrected: '정정됨',
                failed: '재요청 필요',
                retrying: '재평가 중',
                pending: '평가 진행 중',
                missing: 'AI 평가 없음',
                held: '판정 보류',
            }[value] || value;
        },
        fileMeta(file) {
            if (file.attachment_type === 'url') return 'URL';
            const extension = String(file.filename || '').split('.').pop().toUpperCase();
            return extension && extension !== file.filename ? extension : 'FILE';
        },
        async downloadOriginal(file) {
            if (file.attachment_type === 'url') {
                window.open(file.filename, '_blank', 'noopener');
                return;
            }
            if (this.downloadingId != null) return;
            this.downloadingId = file.id;
            try {
                const {data} = await this.$axios.get(`/document-facts/${file.id}/download-url`);
                if (!data?.url) throw new Error('download url unavailable');
                window.open(data.url, '_blank');
            } catch (error) {
                const message = getErrorMessage(error, '원본을 받을 수 없습니다.');
                this.$EventBus.$emit('toast-failure', '다운로드 실패', message);
            } finally {
                this.downloadingId = null;
            }
        },
        async openFacts(file) {
            if (!this.canOpenFacts(file)) return;
            this.factsTitle = file.filename;
            this.factsData = null;
            this.factsModal = true;
            this.factsLoading = true;
            try {
                const {data} = await this.$axios.get(`/document-facts/${file.id}`);
                this.factsData = data;
            } catch (error) {
                const message = getErrorMessage(error, '추출 내용을 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', '조회 오류', message);
                this.factsModal = false;
            } finally {
                this.factsLoading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.ai-review-workspace {
    --review-border: #e4e9f1;
    --review-muted: #6b7890;
    --review-blue: #1769ff;
    --review-column-layout: minmax(310px, 1.02fr) minmax(430px, 1.27fr) minmax(340px, .95fr);
    margin: 0;
    background: #fff;
    color: #24324a;
    min-height: calc(100vh - 68px);
}

.review-toolbar {
    min-height: 66px;
    padding: 12px 20px;
    border-bottom: 1px solid var(--review-border);
    display: grid;
    grid-template-columns: minmax(270px, .95fr) minmax(380px, 1.45fr) auto;
    align-items: center;
    gap: 24px;
    background: #fff;
}
.toolbar-copy { min-width: 0; }
.toolbar-title { font-size: 1.05rem; font-weight: 700; }
.toolbar-subtitle { margin-top: 2px; font-size: .78rem; color: var(--review-muted); }
.toolbar-identity {
    min-width: 0;
    padding-left: 24px;
    border-left: 1px solid var(--review-border);
    display: grid;
    gap: 4px;
}
.identity-row {
    min-width: 0;
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: baseline;
    gap: 10px;
}
.identity-label { color: var(--review-muted); font-size: .7rem; font-weight: 600; }
.identity-value {
    min-width: 0;
    overflow: hidden;
    color: #24324a;
    font-size: .82rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.close-review { border: 1px solid var(--review-border); }

.shared-review-header {
    border-bottom: 1px solid var(--review-border);
    background: #f8f9fb;
}
.review-summary-row {
    min-height: 48px;
    padding: 8px 20px;
    display: grid;
    grid-template-columns: auto minmax(180px, .7fr) minmax(360px, 1.45fr);
    align-items: center;
    gap: 22px;
    border-bottom: 1px solid var(--review-border);
}
.summary-heading { display: flex; align-items: center; gap: 16px; white-space: nowrap; }
.summary-heading strong { font-size: .92rem; }
.summary-heading span { font-size: .86rem; font-weight: 700; }
.review-progress { margin: 0; background: #edf0f5; }
.summary-counts { min-width: 0; display: flex; align-items: center; gap: 14px; color: var(--review-muted); font-size: .72rem; white-space: nowrap; }
.summary-counts b { color: #34445e; }
.summary-percent { margin-left: auto; }
.review-context-row {
    min-height: 74px;
    display: grid;
    grid-template-columns: var(--review-column-layout);
}
.question-context,
.criterion-context {
    min-width: 0;
    padding: 9px 20px 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}
.question-context {
    grid-column: 1;
    border-right: 1px solid var(--review-border);
}
.criterion-context { grid-column: 2 / 4; }
.eyebrow { margin-bottom: 4px; color: var(--review-muted); font-size: .7rem; font-weight: 600; }
.eyebrow--with-level { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.level-badge { border-radius: 999px; font-size: .7rem; font-weight: 700; padding: 3px 9px; }
.level-badge-group { display: inline-flex; align-items: center; gap: 4px; }
.level-badge--stored { color: #7c8798; border: 1px solid #dfe4ec; font-weight: 500; }
.management-level-row { padding: 12px 20px; border-top: 1px solid var(--review-border); background: #fff; }

/* EV_COUNT 방식: 수준별 문항 개수 + 질문 종합 수준 */
.question-level-row { padding: 10px 20px 12px; border-top: 1px solid var(--review-border); background: #fff; }
.qlevel-bar { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); border: 1px solid #d7deea; border-radius: 6px; overflow: hidden; }
.qlevel-cell { min-height: 44px; padding: 6px 8px; display: flex; align-items: center; justify-content: center; gap: 8px; background: #f7f9fc; color: #6b7890; border-right: 1px solid #e6eaf1; font-size: .74rem; }
.qlevel-cell:last-child { border-right: 0; }
.qlevel-cell--active { color: #1c2a42; background: #eaf2ff; box-shadow: inset 0 -3px 0 var(--review-blue); font-weight: 700; }
.qlevel-count { font-size: .95rem; }
.qlevel-foot { margin-top: 6px; display: flex; gap: 14px; color: var(--review-muted); font-size: .7rem; }
.qlevel-note { margin-left: auto; }
.context-value { color: #24324a; font-size: .91rem; line-height: 1.45; font-weight: 700; }
.context-value--question {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;
}
.context-chevron { flex: 0 0 auto; color: #9aa5b4; font-size: .72rem; }
.context-question-title { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.context-value--criterion {
    height: 2.9em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}
.context-value--empty { color: var(--review-muted); font-weight: 500; }

.review-columns {
    display: grid;
    grid-template-columns: var(--review-column-layout);
    min-height: calc(100vh - var(--review-chrome, 256px));
}
.review-column { min-width: 0; background: #fff; }
.answer-column, .criteria-column { border-right: 1px solid var(--review-border); }
.answer-scroll { padding: 18px 20px 32px; max-height: calc(100vh - var(--review-chrome, 256px)); overflow-y: auto; }
.answer-original { padding-bottom: 16px; }
.answer-original ::v-deep .sub-component-title { font-weight: 700; }
.answer-item + .answer-item { margin-top: 24px; }
.answer-item ::v-deep .df-attached-file-url,
.answer-item ::v-deep .df-attached-file-url-sub { display: none; }
.attachments-block { margin-top: 10px; }
.section-title-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.section-title-row h5 { margin: 0; font-size: .92rem; font-weight: 700; }
.section-title-row span { color: var(--review-muted); font-size: .75rem; }
.attachment-list { border: 1px solid var(--review-border); border-radius: 8px; overflow: hidden; }
.attachment-row { min-height: 58px; padding: 9px 10px; display: flex; align-items: center; border-bottom: 1px solid var(--review-border); }
.attachment-row:last-child { border-bottom: 0; }
.attachment-type { width: 28px; color: #ef4c55; text-align: center; }
.attachment-copy { min-width: 0; flex: 1; display: flex; flex-direction: column; }
.attachment-copy strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .8rem; }
.attachment-copy span { color: var(--review-muted); font-size: .69rem; margin-top: 2px; }
.attachment-actions { display: flex; gap: 5px; margin-left: 8px; }
.attachment-actions .btn { width: 34px; height: 34px; padding: 0; border: 1px solid var(--review-border); color: #446082; }
.attachment-action-tooltip { display: inline-flex; }
.attachment-action-tooltip .btn:disabled { opacity: .42; }
.empty-card { padding: 28px 16px; border: 1px dashed #d7deea; border-radius: 8px; text-align: center; color: var(--review-muted); font-size: .8rem; }
.empty-card.compact { padding: 16px; }

.criteria-header { min-height: 38px; display: grid; grid-template-columns: minmax(190px, 1fr) 72px 78px 86px; align-items: center; gap: 6px; padding: 0 18px; border-top: 1px solid var(--review-border); border-bottom: 1px solid var(--review-border); background: #fafbfc; color: var(--review-muted); font-size: .68rem; font-weight: 600; }
.criteria-scroll { max-height: calc(100vh - var(--review-chrome, 256px) - 90px); overflow-y: auto; }
.criteria-group-title { height: 42px; padding: 0 18px; display: flex; align-items: center; justify-content: space-between; background: #fbfcfe; border-bottom: 1px solid var(--review-border); font-size: .79rem; font-weight: 700; }
.criteria-group-title small { color: var(--review-muted); }
.criteria-group-right { display: inline-flex; align-items: center; gap: 10px; }
.apply-sub { padding: 2px 9px; font-size: .68rem; border-radius: 5px; }
.na-final-note { padding: 8px 10px; border: 1px dashed var(--review-border); border-radius: 7px; color: #44536b; font-size: .78rem; font-weight: 700; }
.na-final-note small { display: block; margin-top: 2px; color: var(--review-muted); font-size: .66rem; font-weight: 400; }
.criteria-row { width: 100%; min-height: 68px; display: grid; grid-template-columns: minmax(190px, 1fr) 72px 78px 86px; align-items: center; gap: 6px; padding: 10px 18px; border: 0; border-bottom: 1px solid var(--review-border); background: #fff; text-align: left; color: #32415b; }
.criteria-row:hover { background: #f8faff; }
.criteria-row.selected { background: #f1f6ff; box-shadow: inset 3px 0 0 var(--review-blue); }
.criterion-name { font-size: .78rem; line-height: 1.5; padding-right: 8px; }
.criterion-levels { display: inline-flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.criterion-level { border-radius: 999px; font-size: .62rem; font-weight: 700; }
.criterion-level--muted { color: #8b94a2; border: 1px solid #dfe4ec; background: #fbfcfe; font-weight: 500; }
.criteria-row > span:not(.criterion-name) { text-align: center; }
.judgment-pill, .state-pill { display: inline-flex; justify-content: center; align-items: center; min-height: 24px; padding: 3px 8px; border-radius: 5px; font-size: .69rem; font-style: normal; white-space: nowrap; }
.judgment-satisfied { color: #0c9a58; border: 1px solid #78d7a9; background: #f2fff8; }
.judgment-unsatisfied { color: #ef3947; border: 1px solid #ff9fa6; background: #fff7f7; }
.judgment-not_applicable { color: #6f7784; border: 1px solid #c9ced6; background: #f6f7f8; }
.judgment-pending { color: #8b94a2; background: #f1f3f6; }
.state-pill.unreviewed { color: #7c8798; background: #f2f4f7; }
.state-pill.approved { color: #15774b; background: #eaf9f1; }
.state-pill.corrected { color: #1769ff; background: #eaf2ff; }
.state-pill.failed { color: #c7333f; background: #fff0f1; }
.state-pill.retrying, .state-pill.pending { color: #9a6700; background: #fff7d6; }
.state-pill.missing { color: #687386; background: #f1f3f6; }
.state-pill.held, .judgment-held { color: #9a6700; background: #fff7d6; }
.held-note { padding: 10px 12px; border: 1px solid #f1d98a; border-radius: 7px; background: #fffbea; color: #7a5a00; font-size: .74rem; line-height: 1.55; }
.held-note svg { margin-right: 6px; }
.review-flags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 5px; }
.review-flag { display: inline-block; padding: 1px 6px; border: 1px solid #f1d98a; border-radius: 4px; background: #fffbea; color: #7a5a00; font-size: .64rem; font-weight: 600; line-height: 1.5; white-space: nowrap; }
.review-flag-panel { margin-bottom: 12px; padding: 10px 12px; border: 1px solid #f1d98a; border-radius: 7px; background: #fffdf3; font-size: .74rem; }
.review-flag-panel-head { margin-bottom: 6px; color: #7a5a00; font-weight: 700; }
.review-flag-panel-head small { display: block; color: #8a7a4a; font-weight: 400; }
.review-flag-panel ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 6px; }
.review-flag-note { color: #5b4a1a; line-height: 1.5; }
.ai-basis { margin-top: 14px; padding: 12px 14px; border: 1px solid var(--review-border); border-radius: 8px; background: #fafbfd; }
.ai-basis-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.ai-basis-head > span { color: var(--review-muted); font-size: .72rem; font-weight: 700; }
.ai-basis-comment { margin: 0; white-space: pre-wrap; font-size: .78rem; line-height: 1.6; color: #2c3a52; }
.ai-basis-evidence { margin: 10px 0 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.ai-basis-evidence li { padding: 7px 9px; border-radius: 6px; background: #fff; border: 1px solid var(--review-border); font-size: .72rem; line-height: 1.5; }
.ai-basis-evidence strong { display: block; color: #33425c; word-break: break-all; }
.ai-basis-evidence span { color: var(--review-muted); }
.corrected-cell { color: #a0a8b5; }
.automatic-note { min-height: 52px; padding: 13px 18px; display: flex; gap: 8px; color: var(--review-muted); font-size: .7rem; line-height: 1.5; border-top: 1px solid var(--review-border); }
.automatic-note svg { margin-top: 2px; }

.detail-column { display: flex; flex-direction: column; }
.detail-tabs { height: 42px; flex: 0 0 auto; display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid var(--review-border); }
.detail-tabs button { border: 0; border-bottom: 2px solid transparent; background: #fff; color: var(--review-muted); font-size: .8rem; font-weight: 600; }
.detail-tabs button.active { color: var(--review-blue); border-bottom-color: var(--review-blue); }
.detail-tabs button:disabled { opacity: .5; cursor: not-allowed; }
.detail-chat { height: calc(100vh - var(--review-chrome, 256px) - 42px); min-height: 420px; }
.ai-item-retry-panel, .ai-review-empty { margin: 20px; padding: 20px; border: 1px solid var(--review-border); border-radius: 8px; }
.ai-item-retry-panel { background: #fff8f8; }
.ai-item-retry-panel h4 { margin: 0 0 8px; font-size: .95rem; }
.ai-item-retry-panel p, .ai-review-empty p { color: var(--review-muted); font-size: .76rem; line-height: 1.55; }
.ai-item-retry-panel .btn, .ai-review-empty .btn { margin-top: 8px; }
.ai-review-empty { color: var(--review-muted); font-size: .78rem; line-height: 1.55; text-align: center; background: #fafbfc; }
.detail-scroll { padding: 16px 20px 24px; max-height: calc(100vh - var(--review-chrome, 256px) - 69px); overflow-y: auto; }
.judgment-comparison {
    display: grid;
    grid-template-columns: 116px minmax(0, 1fr);
    margin-bottom: 14px;
    overflow: hidden;
    border: 1px solid var(--review-border);
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(36, 50, 74, .05);
}
.judgment-option {
    min-width: 0;
    min-height: 104px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;
}
.judgment-option--ai { border-right: 1px solid var(--review-border); background: #f8fafc; }
.judgment-comparison span, .derived-result > span { color: var(--review-muted); font-size: .72rem; font-weight: 600; }
.judgment-option--ai .judgment-pill { width: 100%; min-height: 32px; padding: 6px 10px; border-radius: 7px; font-size: .72rem; font-weight: 700; }
.final-judgment-options {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #b9c6d8;
    border-radius: 7px;
    background: #fff;
}
.final-judgment-options ::v-deep .btn {
    min-width: 0;
    min-height: 32px;
    padding: 6px 5px;
    margin-left: 0 !important;
    border: 0;
    border-right: 1px solid #d5ddea;
    border-radius: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #486079;
    background: #fff;
    box-shadow: none !important;
    font-size: .69rem;
}
.final-judgment-options ::v-deep .btn:last-child { border-right: 0; }
.final-judgment-options ::v-deep .btn:hover:not(.active) { color: var(--review-blue); background: #f3f7ff; }
.final-judgment-options ::v-deep .btn.active { color: #fff; background: var(--review-blue); font-weight: 700; }
.final-judgment-options ::v-deep .btn.focus,
.final-judgment-options ::v-deep .btn:focus-visible { box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .85) !important; }
.derived-result { padding: 10px 12px; border: 1px solid #d9e6ff; border-radius: 7px; background: #f3f7ff; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.derived-result > .badge { color: #fff; font-weight: 700; }
.derived-result small { width: 100%; color: var(--review-muted); font-size: .68rem; }
.feedback-section { margin-top: 20px; }
.feedback-section > label { display: block; margin-bottom: 3px; font-size: .8rem; font-weight: 700; }
.feedback-section > label span, .feedback-section > small { color: var(--review-muted); font-size: .68rem; font-weight: 400; }
.feedback-section > small { display: block; margin-bottom: 9px; }
.reason-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.reason-chips button { border: 1px solid var(--review-border); border-radius: 6px; background: #fff; color: #55637a; padding: 6px 9px; font-size: .7rem; }
.reason-chips button.selected { border-color: var(--review-blue); background: var(--review-blue); color: #fff; font-weight: 700; }
.reason-mini { margin-top: 8px; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 6px; }
.evidence-list { display: flex; flex-direction: column; gap: 7px; }
.evidence-list > button { width: 100%; display: flex; gap: 8px; padding: 9px 10px; border: 1px solid var(--review-border); border-radius: 6px; background: #fff; color: #43516a; text-align: left; }
.evidence-list > button.selected { border-color: var(--review-blue); background: #f3f7ff; }
.evidence-list ::v-deep .custom-control { min-height: 1rem; padding-left: 1.25rem; pointer-events: none; }
.evidence-list button > span { min-width: 0; display: flex; flex-direction: column; }
.evidence-list strong { font-size: .72rem; }
.evidence-list em { margin-top: 3px; color: var(--review-muted); font-size: .67rem; font-style: normal; line-height: 1.45; }
.character-count { margin-top: 3px; color: var(--review-muted); text-align: right; font-size: .65rem; }
.approval-note { margin-top: 18px; padding: 14px; border: 1px solid #c9ebda; border-radius: 7px; background: #f3fcf7; color: #287353; font-size: .75rem; }
.approval-note svg { margin-right: 6px; }
.sub-request { margin: 0 20px 12px; padding: 10px 12px; border: 1px solid var(--review-border); border-radius: 7px; background: #fafbfc; font-size: .74rem; }
.sub-request summary { color: var(--review-muted); font-weight: 600; cursor: pointer; }
.sub-request[open] summary { margin-bottom: 8px; }
.sub-request small { margin-bottom: 4px; }
.poll-timeout-note { margin: 0 20px 12px; padding: 10px 12px; border: 1px solid #f1d98a; border-radius: 7px; background: #fffbea; color: #7a5a00; font-size: .74rem; }
.poll-timeout-note p { margin-bottom: 6px; line-height: 1.5; }
.detail-actions { margin-top: auto; padding: 12px 20px 18px; border-top: 1px solid var(--review-border); background: #fff; }
.detail-actions small { display: block; color: var(--review-muted); font-size: .65rem; margin-bottom: 9px; }
.detail-action-buttons { display: grid; grid-template-columns: auto minmax(180px, 1fr); gap: 8px; }
.empty-detail { min-height: 540px; padding: 50px 36px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--review-muted); text-align: center; }
.empty-detail > svg { font-size: 1.8rem; margin-bottom: 14px; color: #9aa8ba; }
.empty-detail strong { color: #44536b; margin-bottom: 8px; }
.empty-detail span { font-size: .75rem; line-height: 1.55; }
.facts-viewer { display: flex; flex-direction: column; gap: 20px; }
.fact-entry { border: 1px solid var(--review-border); border-radius: 8px; overflow: hidden; }
.fact-entry-title { padding: 12px 14px; display: flex; gap: 8px; align-items: center; background: #f7f9fc; }
.fact-row { display: grid; grid-template-columns: 130px 1fr; border-top: 1px solid var(--review-border); }
.fact-row > span { padding: 12px; color: var(--review-muted); font-size: .72rem; background: #fbfcfd; }
.fact-row > p { margin: 0; padding: 12px 14px; white-space: pre-wrap; font-size: .78rem; line-height: 1.65; }

@media (max-width: 1280px) {
    .ai-review-workspace { --review-column-layout: minmax(280px, .9fr) minmax(400px, 1.2fr) minmax(320px, .9fr); }
    .review-toolbar { grid-template-columns: minmax(250px, .85fr) minmax(330px, 1.15fr) auto; gap: 16px; padding-right: 14px; padding-left: 14px; }
    .toolbar-identity { padding-left: 16px; }
    .review-summary-row { grid-template-columns: auto minmax(150px, .55fr) minmax(340px, 1.45fr); gap: 16px; padding-right: 14px; padding-left: 14px; }
    .summary-counts { gap: 9px; }
    .criteria-header, .criteria-row { grid-template-columns: minmax(160px, 1fr) 66px 70px 78px; padding-left: 12px; padding-right: 12px; }
}
</style>
